package screens.list

import common.EConstants
import common.ECssClasses
import components.ButtonContent
import components.Toolbar
import components.alert.Alert
import components.alert.AlertItem
import components.burgerMenu.BurgerMenu
import components.colourToneItem.ColourToneItem
import components.items.WatchlistIcon
import components.toast.BottomToast
import components.toast.ToastItem
import core.AppContext
import core.enums.EImages
import core.enums.ELocalizationKeys
import core.extensions.toClassName
import core.extensions.useCoroutineScope
import core.io.CustomHttpClient
import core.ionic.IonButton
import core.ionic.IonCol
import core.ionic.IonContent
import core.ionic.IonGrid
import core.ionic.IonHeader
import core.ionic.IonPage
import core.ionic.IonRow
import core.localStorage.LocalStorageDAO
import core.localization.Localization
import core.piwik.Piwik
import csstype.Display
import csstype.FlexDirection
import csstype.JustifyContent
import csstype.Margin
import csstype.Overflow
import csstype.number
import csstype.pct
import csstype.px
import emotion.react.css
import kotlinx.coroutines.launch
import module.watchlist.ColourListObject
import module.watchlist.toMinifiedColourDTO
import react.FC
import react.Props
import react.dom.html.ReactHTML
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.router.Navigate
import react.router.useNavigate
import react.useContext
import react.useEffectOnce
import react.useMemo
import react.useState
import screens.EScreens

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 06.10.22
 * Author : mariorosshirt
 */

external interface ColourToneListScreenProps : Props {
}

val ColourToneListScreen = FC<ColourToneListScreenProps> { props ->
    val mContext by useContext(AppContext)
    val (mLocalStorage, mLocalStorageDispatch) = mContext.mLocalStorageReducer
    val mClient = CustomHttpClient()
    val mOwnColourList: List<ColourListObject> = mLocalStorage.mFixedList.map {
        ColourListObject(
            it.mUnique,
            it.mName,
            it.mArtNr,
            it.mColourCode,
            null,
            true,
            it.mColourCollection,
            it.mAdditionalInfo1,
            it.mAdditionalInfo2,
            it.mEanCode
        )
    }
    val mSuggestedColourList: List<ColourListObject> = mLocalStorage.mSuggestedList.map {
        ColourListObject(
            it.mUnique,
            it.mName,
            it.mArtNr,
            it.mColourCode,
            null,
            false,
            it.mColourCollection,
            it.mAdditionalInfo1,
            it.mAdditionalInfo2,
            it.mEanCode
        )
    }
    val mNavigate = useNavigate()
    val mCompleteColourList = useMemo(mOwnColourList, mSuggestedColourList) {
        mOwnColourList.toMutableList().plus(mSuggestedColourList)
    }
    val mScope = useCoroutineScope()
    var mToastItem: ToastItem? by useState(null)
    var mAlertItem: AlertItem? by useState(null)

    useEffectOnce {
        if(mSuggestedColourList.isEmpty()) {
            mScope.launch {
                mClient.getAdditionalImage(mOwnColourList.map { it.mUnique }).forEach {
                    mLocalStorageDispatch.invoke(
                        LocalStorageDAO.ELocalStorageAction.ADD_TO_SUGGESTEDLIST(it)
                    )
                }
            }
        }
    }

    var mTargetScreen: String? by useState(null)

    fun deleteFromFixedList(colour: ColourListObject) {
        mLocalStorageDispatch.invoke(
            LocalStorageDAO.ELocalStorageAction.DELETE_FROM_FIXEDLIST(
                colour.toMinifiedColourDTO()
            )
        ).also {
            mToastItem = ToastItem(
                Localization.getLocalizationKey(
                    ELocalizationKeys.CORE_TOAST_DELETE_FROM_FIXED_OR_SUGGESTED_LIST
                )
            )
        }
    }

    fun deleteFromSuggestedList(colour: ColourListObject) {
        mLocalStorageDispatch.invoke(
            LocalStorageDAO.ELocalStorageAction.DELETE_FROM_SUGGESTEDLIST(colour.toMinifiedColourDTO())
        ).also {
            mToastItem = ToastItem(
                Localization.getLocalizationKey(
                    ELocalizationKeys.CORE_TOAST_DELETE_FROM_FIXED_OR_SUGGESTED_LIST
                )
            )
        }
    }

    BurgerMenu()


    IonPage {
        id = EConstants.BURGER_MENU_CONTENT.mId
        IonHeader {
            Toolbar {
                mEndViews = listOf(WatchlistIcon)
            }
        }
        IonContent {
            IonGrid {
                css(ECssClasses.VERTICAL_GRID.toClassName(), ECssClasses.NON_SCROLLABLE_GRID.toClassName()) {}
                IonRow {
                    css() {
                        flexGrow = number(1.0)
                        overflow = Overflow.scroll
                    }
                    IonCol {
                        div {
                            css() {
                                height = 100.pct
                                display = Display.flex
                                flexDirection = FlexDirection.column
                            }
                            if(mOwnColourList.isNotEmpty()) {
                                span {
                                    css(ECssClasses.LIST_HEADER_FONT.toClassName()) {}
                                    +Localization.getLocalizationKey(ELocalizationKeys.S04_OWN_COLOURS_HEADER.name)
                                }
                            }
                            mOwnColourList.forEach { colour ->
                                ColourToneItem {
                                    mColourObject = colour
                                    mColourToneItemLeftButtonToastMessage = { toastItem ->
                                        mToastItem = toastItem
                                    }
                                    mDeleteFunction = {
                                        if(mCompleteColourList.size != 1) {
                                            deleteFromFixedList(colour)
                                        } else {
                                            mAlertItem = AlertItem.confirmDeleteAlertItem(
                                                Localization.getLocalizationKey(
                                                    ELocalizationKeys.S04_DELETE_CONFIRMATION_TEXT
                                                )
                                            ) {
                                                deleteFromFixedList(colour)
                                            }
                                        }
                                    }
                                }
                            }
                            mSuggestedColourList.let {
                                if(it.isNotEmpty())
                                    span {
                                        css(ECssClasses.LIST_HEADER_FONT.toClassName()) {}
                                        +Localization.getLocalizationKey(ELocalizationKeys.S04_SUGGESTED_COLOURS_HEADER.name)
                                    }
                            }
                            mSuggestedColourList.forEach { colour ->
                                ColourToneItem {
                                    mColourObject = colour
                                    mColourToneItemLeftButtonToastMessage = { toastItem ->
                                        mToastItem = toastItem
                                    }
                                    mDeleteFunction = {
                                        if(mCompleteColourList.size != 1) {
                                            deleteFromSuggestedList(colour)
                                        } else {
                                            mAlertItem = AlertItem.confirmDeleteAlertItem(
                                                Localization.getLocalizationKey(
                                                    ELocalizationKeys.S04_DELETE_CONFIRMATION_TEXT
                                                )
                                            ) {
                                                deleteFromSuggestedList(colour)
                                            }
                                        }
                                        Piwik.trackContentInteraction(
                                            Piwik.CATEGORY_COLORTONE_LIST,
                                            Piwik.NAME_COLORTONE_LIST_DELETE_COLOR,
                                            colour.mUnique,
                                            Piwik.TARGET_COLOR
                                        )
                                    }
                                }

                            }
                            if(mOwnColourList.isEmpty() && mSuggestedColourList.isEmpty()) {
                                ReactHTML.div {
                                    css(ECssClasses.LIST_NOTE_STYLE.toClassName()) {
                                        marginTop = 8.px
                                    }
                                    ReactHTML.div {
                                        css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) {

                                        }
                                        +Localization.getLocalizationKey(ELocalizationKeys.S04_FIXED_LIST_EMPTY_TITLE.name)
                                    }
                                    ReactHTML.div {
                                        css {
                                            marginTop = 8.px
                                        }
                                        +Localization.getLocalizationKey(ELocalizationKeys.S04_FIXED_LIST_EMPTY_TEXT.name)
                                    }
                                    ReactHTML.div {
                                        css {
                                            display = Display.flex
                                            justifyContent = JustifyContent.center
                                        }

                                        IonButton {
                                            css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                                width = 165.px
                                                marginTop = 42.px
                                            }
                                            onClick = {
                                                mTargetScreen = EScreens.COLOUR_FINDER_UPLOAD_SCREEN.mPath
                                            }
                                            ButtonContent {
                                                mContent =
                                                    Localization.getLocalizationKey(ELocalizationKeys.S04_FIXED_LIST_EMPTY_BUTTON_CONTENT.name)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    mToastItem?.let {
                        BottomToast {
                            this.mToastItem = it
                        }
                    }
                }
                IonRow {
                    css {
                        margin = Margin(top = 28.px, horizontal = 20.px, bottom = 0.px)
                    }
                    IonCol {
                        css {
                            padding = 0.px
                        }
                        size = "12"
                        IonButton {
                            css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                display = Display.block
                            }
                            disabled = mCompleteColourList.isEmpty()
                            onClick = {
                                mTargetScreen = EScreens.WALL_COLOURER_UPLOAD_SCREEN.mPath
                            }
                            ButtonContent {
                                mLeftImage = EImages.PAINT_ROLLER_SMALL
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.S04_PRIMARY_ACTION_BUTTON_CONTENT.name)
                            }
                        }
                    }
                }
                IonRow {
                    css(ECssClasses.BOTTOM_BUTTONS.toClassName()) {}
                    IonCol {
                        css {
                            justifyContent = JustifyContent.spaceBetween
                            display = Display.flex
                            padding = 0.px
                        }
                        IonButton {
                            css(ECssClasses.SECONDARY_BUTTON.toClassName()) {
                                width = 165.px
                            }
                            onClick = {
                                mNavigate.invoke(-1)
                            }
                            ButtonContent {
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.CORE_BACK.name)
                            }
                        }
                        IonButton {
                            css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                width = 165.px
                                //marginBottom = 32.px
                            }
                            onClick = {
                                mLocalStorageDispatch.invoke(
                                    LocalStorageDAO.ELocalStorageAction.ADD_COLOUR_LIST_TO_PRODUCTLIST(
                                        mCompleteColourList
                                    )
                                )
                                mTargetScreen = EScreens.PRODUCT_LIST_SCREEN.mPath
                            }
                            disabled = mOwnColourList.isEmpty() && mSuggestedColourList.isEmpty()
                            ButtonContent {
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.S04_PRIMARY_NAVIGATION_BUTTON_CONTENT.name)
                            }
                        }
                    }
                }
            }
        }

        mTargetScreen?.let {
            Navigate {
                to = it
            }
        }
    }
    mAlertItem?.let {
        Alert {
            this.mAlertItem = it
        }
    }
}